import { Box, Card, CardContent, Grid, styled, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { FC, useContext, useEffect, useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { SiteEventType } from '../api/dtos/dtos';
import EmailNotificationCard from '../components/datamanagement/EmailNotificationCard';
import MembershipCard from '../components/datamanagement/MembershipCard';
import RecordsCard from '../components/datamanagement/RecordsCard';
import Summary from '../components/datamanagement/SummaryCard';
import FilterButton from '../components/map/FilterButton';
import AuthContext from '../contexts/auth/AuthContext';
import MapContext, { MapFilterPeriodEnum, MapFilterSubmitter } from '../contexts/data/MapContext';
import { SpeciesContext } from '../contexts/data/SpeciesContext';
import UserGroupsContext from '../contexts/data/UserGroupsContext';
import isCurrentGroupAdmin from '../functions/user';
import useGetSiteEventGetCollection from '../hooks/data/useGetSiteEventGetCollection';
import isValidDateRange from '../functions/date';
import useGetExternalRecordCount from '../hooks/data/useGetExternalRecordCount';

const ContentWrapper = styled(Box)(({ theme }) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.background.default,
}));

const DataManagementContent: FC = () => {

    //
    // -- Context --
    //
    const { filter, closeDrawer } = useContext(MapContext);
    const { user } = useContext(AuthContext);
    const { species: {species} } = useContext(SpeciesContext);
    const { data: userGroupsConfig } = useContext(UserGroupsContext);

    const isAdminOrSuperAdmin = useMemo(() => {
        return !!user?.roles?.find(x => x === 'admin' || x === 'superadmin');
    }, [user]);

    const isDataAccess = useMemo(() => {
        return !!user?.roles?.find(x => x === 'dataaccess');
    }, [user]);

    const isDataAccessForThisSpecies = useMemo(() => {
        return (
            user?.matchingRegionAdminRoles?.some(
                x => x.speciesId == null || x.speciesId === species
            ) ?? false
        );
    }, [user?.matchingRegionAdminRoles, species]);

    const isGroupAdmin = useMemo(() => {
        return isCurrentGroupAdmin(userGroupsConfig, user, filter);
    }, [userGroupsConfig, user, filter, isCurrentGroupAdmin]);

    // Cleanup
    useEffect(() => {
        return () => {
            closeDrawer();
        }
    }, []);

    // External record request
    const externalRecordCountRequest = useMemo(() => {

        if (!isAdminOrSuperAdmin && !isDataAccess && ! isDataAccessForThisSpecies) {
            return;
        }

        if (!species) {
            return undefined;
        }

        if (!isValidDateRange(filter.reportingPeriod.fromField, filter.reportingPeriod.toField)) {
            return undefined;
        }

        // Period
        let from = null;
        let to = null;
        switch (filter.reportingPeriod.period) {
            case MapFilterPeriodEnum.DateRange: 
                from = filter.reportingPeriod.fromField;
                to = filter.reportingPeriod.toField;
                break;

            case MapFilterPeriodEnum.Year: 
                const year = filter.reportingPeriod.yearField;
                from = year?.startOf('year');

                // If the year is equal to this year, we want to cap off
                // at the current date.
                if (year?.year() === dayjs().year()) {
                    to = dayjs();
                } else {
                    to = year?.endOf('year');
                }

                break;
                
            case MapFilterPeriodEnum.MonthYear: 
                const monthYear = filter.reportingPeriod.monthYearField;
                from = monthYear?.startOf('month');

                // If the year is equal to this year, we want to cap off
                // at the current date.
                if (monthYear?.year() === dayjs().year() && monthYear?.month() === dayjs().month()) {
                    to = dayjs();
                } else {
                    to = monthYear?.endOf('month');
                }

                break;

            case MapFilterPeriodEnum.LastSevenDays: 
                from = dayjs().subtract(7, 'days');  
                to = dayjs();  
                break;

            case MapFilterPeriodEnum.LastThirtyOneDays: 
                from = dayjs().subtract(31, 'days');  
                to = dayjs();  
                break;
        }
        
        return {
            species: species, 
            startDateOnOrAfter: from?.format('DD/MM/YYYY'),
            startDateOnOrBefore: to?.format('DD/MM/YYYY'),
            locatedInRegionIds: (!isAdminOrSuperAdmin && (isDataAccessForThisSpecies || isDataAccess)) ? filter.bySubmitter.locatedInRegionIds : undefined,
        };
    }, [species, filter, isAdminOrSuperAdmin, isDataAccessForThisSpecies, isDataAccess]);

    // Generate the site events request based off map filter values
    const siteEventsRequest = useMemo(() => {

        if (!species) {
            return undefined;
        }

        if (!isValidDateRange(filter.reportingPeriod.fromField, filter.reportingPeriod.toField)) {
            return undefined;
        }

        // Period
        let from = null;
        let to = null;
        switch (filter.reportingPeriod.period) {
            case MapFilterPeriodEnum.DateRange: 
                from = filter.reportingPeriod.fromField;
                to = filter.reportingPeriod.toField;
                break;

            case MapFilterPeriodEnum.Year: 
                const year = filter.reportingPeriod.yearField;
                from = year?.startOf('year');

                // If the year is equal to this year, we want to cap off
                // at the current date.
                if (year?.year() === dayjs().year()) {
                    to = dayjs();
                } else {
                    to = year?.endOf('year');
                }

                break;
                
            case MapFilterPeriodEnum.MonthYear: 
                const monthYear = filter.reportingPeriod.monthYearField;
                from = monthYear?.startOf('month');

                // If the year is equal to this year, we want to cap off
                // at the current date.
                if (monthYear?.year() === dayjs().year() && monthYear?.month() === dayjs().month()) {
                    to = dayjs();
                } else {
                    to = monthYear?.endOf('month');
                }

                break;

            case MapFilterPeriodEnum.LastSevenDays: 
                from = dayjs().subtract(7, 'days');  
                to = dayjs();  
                break;

            case MapFilterPeriodEnum.LastThirtyOneDays: 
                from = dayjs().subtract(31, 'days');  
                to = dayjs();  
                break;
        }

        switch (filter.bySubmitter.submitter) {
            case MapFilterSubmitter.AllData:
                return {
                    species: species, 
                    startDateOnOrAfter: from?.format('DD/MM/YYYY'),
                    startDateOnOrBefore: to?.format('DD/MM/YYYY'),
                    locatedInRegionIds: (!isAdminOrSuperAdmin && isDataAccessForThisSpecies) ? filter.bySubmitter.locatedInRegionIds : undefined,
                    dataAccessId: (!isAdminOrSuperAdmin && isDataAccessForThisSpecies) ? filter.bySubmitter.dataAccessId : undefined,
                };
            case MapFilterSubmitter.PublicData:
                return {
                    species: species,
                    onlyPublicData: true,
                    startDateOnOrAfter: from?.format('DD/MM/YYYY'),
                    startDateOnOrBefore: to?.format('DD/MM/YYYY')
                };
            case MapFilterSubmitter.MyData:
                return {
                    species: species,
                    userId: user?.id,
                    startDateOnOrAfter: from?.format('DD/MM/YYYY'),
                    startDateOnOrBefore: to?.format('DD/MM/YYYY')
                };
            case MapFilterSubmitter.MyGroup:
                if (!filter.bySubmitter.communityGroupId) {
                    return undefined;
                }
                return {
                    species: species,
                    userId: filter.bySubmitter.communityGroupMemberId != -1 ? filter.bySubmitter.communityGroupMemberId : undefined,
                    startDateOnOrAfter: from?.format('DD/MM/YYYY'),
                    startDateOnOrBefore: to?.format('DD/MM/YYYY'),
                    memberOfCommunityGroupIds: filter.bySubmitter.communityGroupId ? [filter.bySubmitter.communityGroupId] : undefined
                };
        }
    }, [user, filter, species]);

    // Get site event requests
    const { data: siteEventsSighting, loading: siteEventsSightingLoading }  = useGetSiteEventGetCollection(
        filter.recordType.sighting && siteEventsRequest ? 
        {
            ...siteEventsRequest,
            siteEventType: SiteEventType.Sighting
        } : 
        undefined,
        { revalidateOnFocus: false, errorRetryCount: 0 }
    );
    const { data: siteEventsDamage, loading: siteEventsDamageLoading } = useGetSiteEventGetCollection(
        filter.recordType.damage && siteEventsRequest ? 
        {
            ...siteEventsRequest,
            siteEventType: SiteEventType.Damage
        } : 
        undefined, 
        { revalidateOnFocus: false, errorRetryCount: 0 }
    );
    const { data: siteEventsControl, loading: siteEventsControlLoading } = useGetSiteEventGetCollection(
        filter.recordType.control && siteEventsRequest ? 
        {
            ...siteEventsRequest,
            siteEventType: SiteEventType.Control
        } :
        undefined, 
        { revalidateOnFocus: false, errorRetryCount: 0 }
    );
    const { data: siteEventsDisease, loading: siteEventsDiseaseLoading } = useGetSiteEventGetCollection(
        filter.recordType.disease && siteEventsRequest ? 
            {
                ...siteEventsRequest,
                siteEventType: SiteEventType.Disease
            } : 
            undefined, 
        { revalidateOnFocus: false, errorRetryCount: 0 }
    );

    const { data: externalRecordCount, loading: externalRecordCountLoading } = useGetExternalRecordCount(
        filter.recordType.externallySourcedData && externalRecordCountRequest ? 
            {
                ...externalRecordCountRequest,
            } : 
            undefined, 
        { revalidateOnFocus: false, errorRetryCount: 0 }
    );

    const getTitle = () => {
        if (isGroupAdmin){
            return "Data and Group Management"
        }

        return "Data Management"
    }

    return (
        <Box>
            <ContentWrapper>
                {/* Header */}
                <Typography fontSize={32} color={"#FFFFFF"} sx={{paddingTop: "15px", paddingBottom: "15px"}}>{getTitle()}</Typography> 

                {/* Map Filter */}
                <Box style={{position: 'fixed', right: "0", padding: '16px', zIndex: 1}}>
                    <FilterButton 
                        loading={false}
                        disabled={false}
                    />
                </Box>

                {/* Group Admin description/instructions */}
                {isGroupAdmin && (
                    <>
                        <Card variant='outlined'>
                        <CardContent>
                            <Grid container spacing={1} alignItems={"center"}>
                                <Grid item xs={12}>
                                    <Box display="flex" alignItems={"center"}>
                                        <Typography paddingLeft={"5px"} fontSize={16}>
                                            Welcome to your group admin page.
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box display="flex" alignItems={"center"}>
                                        <Typography paddingLeft={"5px"} fontSize={16}>
                                            This page enables you to view your group's records as ‘Charts and Tables’, download your group's data as a .CSV format, manage the membership, and view the email notifications that exist for your group.
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                        </Card>
                        <Box height={'25px'} />
                    </>
                )}

                {/* Group Cards */}
                {filter.bySubmitter.submitter === MapFilterSubmitter.MyGroup && (
                    <>
                        <MembershipCard />
                        <Box height={'25px'} />
                        <EmailNotificationCard />
                        <Box height={'25px'} />
                    </>
                )}

                {/* Summary */}
                <Summary 
                    siteEventsSighting={siteEventsSighting} 
                    siteEventsSightingLoading={siteEventsSightingLoading} 
                    siteEventsDamage={siteEventsDamage} 
                    siteEventsDamageLoading={siteEventsDamageLoading} 
                    siteEventsControl={siteEventsControl} 
                    siteEventsControlLoading={siteEventsControlLoading}
                    siteEventsDisease={siteEventsDisease} 
                    siteEventsDiseaseLoading={siteEventsDiseaseLoading}
                    externalRecordCount={externalRecordCount}
                    externalRecordCountLoading={externalRecordCountLoading}
                />

                
                <Box height={'25px'} />

                {/* Record Tables */}
                {!isDataAccess && filter.recordType.sighting && (
                    <>
                        <RecordsCard 
                            title={'Sighting Records'} 
                            siteEventType={SiteEventType.Sighting}
                            totalRecords={siteEventsSighting?.totalCount ?? 0}                    
                        />

                        <Box height={'25px'} />
                    </>
                )}
                {!isDataAccess && filter.recordType.damage && (
                    <>
                        <RecordsCard 
                            title={'Damage Records'} 
                            siteEventType={SiteEventType.Damage}
                            totalRecords={siteEventsDamage?.totalCount ?? 0}                      
                        />

                        <Box height={'25px'} />
                    </>
                )}
                {!isDataAccess && filter.recordType.control && (
                    <>
                        <RecordsCard 
                            title={'Control Records'} 
                            siteEventType={SiteEventType.Control}
                            totalRecords={siteEventsControl?.totalCount ?? 0}                     
                        />
                        <Box height={'25px'} />
                    </>
                )}

                {!isDataAccess && filter.recordType.disease && (
                    <>
                        <RecordsCard 
                            title={'Disease Records'} 
                            siteEventType={SiteEventType.Disease}
                            totalRecords={siteEventsDisease?.totalCount ?? 0}                 
                        />
                        <Box height={'25px'} />
                    </>
                )}
                
            </ContentWrapper>
        </Box>
    )
}

const DataManagementPage: FC = () => {
    const { user } = useContext(AuthContext);
    const { species: speciesName } = useParams();

    if (!user) {
        return <Navigate to={"/" + speciesName + "/map"} replace />
    }
    return (<DataManagementContent />)
}

export default DataManagementPage
